import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useSettings } from '../../context/SettingsContext';
import { MessageThread, ReplyBox } from '../../components/inquiries/MessageThread';
import { useInquiryCategories, categoryLabel, TYPE_LABELS, formatWhen } from '../../components/inquiries/inquiryShared';

const STATUS_TEXT = { new: 'Waiting for review', contacted: 'In progress', completed: 'Completed', declined: 'Closed' };

export default function InquiryThreadPage() {
  const { token } = useParams();
  const { settings } = useSettings();
  const categories = useInquiryCategories();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() =>
    axios.get(`/api/inquiries/thread/${token}`).then(r => setData(r.data))
      .catch(e => setError(e.response?.status === 404 ? 'This conversation link is not valid.' : 'Could not load your conversation.')),
  [token]);

  // Check for new replies while the page is open
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 15000);
    return () => clearInterval(t);
  }, [load]);

  const send = async ({ body, images }) => {
    const r = await axios.post(`/api/inquiries/thread/${token}/messages`, { body, images });
    setData(d => ({ ...d, messages: r.data.messages }));
  };

  if (error) return <div className="container" style={{ padding: '60px 20px', textAlign: 'center' }}><div className="alert alert-error">{error}</div></div>;
  if (!data) return <div className="loading"><div className="spinner" /></div>;
  const { inquiry, messages } = data;

  return (
    <div>
      <div className="page-hero" style={{ padding: '32px 0' }}>
        <h1>Your Inquiry</h1>
        <p>{STATUS_TEXT[inquiry.status] || inquiry.status}{inquiry.handled_by ? ` · ${inquiry.handled_by} is helping you` : ''}</p>
      </div>
      <div className="container" style={{ padding: '32px 16px', maxWidth: 720 }}>
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: '#9a9a9a', marginBottom: 6 }}>
            {TYPE_LABELS[inquiry.inquiry_type]} · {categoryLabel(categories, inquiry.item_category)} · {formatWhen(inquiry.created_at)}
          </div>
          <p style={{ fontSize: 14, color: '#444', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{inquiry.item_description}</p>
          {inquiry.images.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {inquiry.images.map(img => (
                <a key={img.id} href={img.url} target="_blank" rel="noreferrer"><img src={img.url} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8 }} /></a>
              ))}
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 20 }}>
          <MessageThread messages={messages} viewer="customer"
            emptyText="No replies yet. We'll email you when someone on our team responds — you can add more details or photos below in the meantime." />
          <div style={{ borderTop: '1px solid #eee', marginTop: 16, paddingTop: 16 }}>
            <ReplyBox onSend={send} placeholder="Add details, ask a question, or send more photos…" />
          </div>
        </div>
        <p style={{ fontSize: 12, color: '#9a9a9a', marginTop: 16, textAlign: 'center' }}>
          Bookmark this page — it's your private link to this conversation. Prefer to talk? Call {settings.store_phone}.
        </p>
      </div>
    </div>
  );
}
